import React, { useState, Suspense, lazy } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { X, Loader2 } from 'lucide-react';
import { getLastSubmissionData, resetAllStorageData } from '../utils/storage';
import { NewsSection } from '../components/NewsSection';

// Lazy load non-critical components
const FeaturedPlaylists = lazy(() => import('../components/FeaturedPlaylists').then(module => ({ default: module.FeaturedPlaylists })));
const SubmissionModal = lazy(() => import('../components/SubmissionModal').then(module => ({ default: module.SubmissionModal })));

// Split into smaller components
const HeroSection = () => (
  <div className="bg-gradient-to-b from-green-50 to-white">
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Free Spotify Playlist Submissions - No Signup
          </h1>
          <h2 className="text-xl text-gray-600">
            Connect Instantly with Thousands of Independent Playlist Curators
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 text-left">
          <div className="bg-white border border-gray-200 p-8 hover:border-green-500 transition-colors rounded-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Direct Connections</h3>
            <p className="text-gray-600">
              Skip the middlemen and connect with real playlist curators who are passionate about discovering new music. Our platform eliminates gatekeepers, letting your music speak directly to those who can share it with their audiences.
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-8 hover:border-green-500 transition-colors rounded-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Zero Barriers</h3>
            <p className="text-gray-600">
              No accounts, no credits—just instant Spotify submissions without hassle. Paste your track link and start submitting to playlists immediately. We've removed every obstacle between independent artists and playlist opportunities.
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-8 hover:border-green-500 transition-colors rounded-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Community-Driven</h3>
            <p className="text-gray-600">
              Built by independent musicians for the indie community. Our platform fosters genuine relationships between artists and curators based on mutual appreciation rather than transactional expectations.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FeaturedSection = () => {
  const lastSubmission = getLastSubmissionData();
  
  return (
    <div className="my-8 pt-8 border-t border-gray-200">
      <div className="max-w-[1400px] mx-auto">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Today's Featured Playlists - {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </h2>
        
        {lastSubmission?.lastTrack && (
          <div className="mb-6 flex items-center justify-between bg-white p-4 shadow-sm rounded-lg">
            <div className="flex items-center gap-4">
              <img
                src={lastSubmission.lastTrack.album.images[2]?.url}
                alt={lastSubmission.lastTrack.name}
                className="w-12 h-12 object-cover rounded-md"
                loading="lazy"
              />
              <div>
                <p className="font-medium">{lastSubmission.lastTrack.name}</p>
                <p className="text-sm text-gray-600">
                  {lastSubmission.lastTrack.artists[0]?.name}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                resetAllStorageData();
                window.location.reload();
              }}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Clear selected track"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        )}

        <div className="border-l-4 border-green-500 pl-6">
          <Suspense fallback={<div className="h-32 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>}>
            <FeaturedPlaylists />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const CTASection = () => (
  <div className="bg-gradient-to-b from-gray-50 via-gray-100 to-gray-50 py-12 mt-16 mb-16 border-y border-gray-200">
    <div className="text-center max-w-[1400px] mx-auto px-4">
      <p className="text-gray-800 text-xl font-medium mb-6">Find the Perfect Playlist for Your Music</p>
      <Link
        to="/playlists"
        className="inline-block px-12 py-5 bg-blue-600 text-white text-xl font-semibold rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
      >
        Explore All Playlists
      </Link>
    </div>
  </div>
);

export function HomePage() {
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);

  return (
    <>
      <SEO />
      <main>
        <HeroSection />
        
        <div className="container mx-auto px-4 pb-12">
          <Suspense fallback={null}>
            <FeaturedSection />
          </Suspense>
          
          <CTASection />
          
          {/* News Section */}
          <NewsSection />
          
          {/* FAQ and Additional Info sections are now lazy loaded */}
          <Suspense fallback={null}>
            <LazyFAQSection />
            <LazyAdditionalInfoSection />
          </Suspense>
        </div>

        {selectedPlaylist && (
          <Suspense fallback={null}>
            <SubmissionModal
              playlist={selectedPlaylist}
              onClose={() => setSelectedPlaylist(null)}
              onSubmit={() => setSelectedPlaylist(null)}
            />
          </Suspense>
        )}
      </main>
    </>
  );
}

// Lazy load FAQ and Additional Info sections
const LazyFAQSection = lazy(() => import('./FAQSection'));
const LazyAdditionalInfoSection = lazy(() => import('./AdditionalInfoSection'));